import { RequestStatus } from 'Constants';
import MvSelect from 'Generic/MvSelect';
import { MvSortTable } from 'Generic/MvSortTable';
import { TableRow } from 'Generic/MvSortTable/MvSortTable';
import MvSpinner from 'Generic/MvSpinner';
import { MvTextOrEdit } from 'Generic/MvTextOrEdit';
import PointsCalendar from 'Generic/PointsCalendar';
import ResortDropdown from 'Generic/ResortDropdown/ResortDropdown';
import CalculatorFormData from 'Interfaces/CalculatorFormDataInterface';
import { ResortPointInterface } from 'Interfaces/ResortPointInterface';
import SearchCriteria from 'Interfaces/SearchCriteriaInterface';
import Validators from 'Interfaces/ValidatorsInterface';
import { sortResortPointCalculator, SortType, SortDirection } from "Utils/SortResortPointCalculator";
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import MvModal from '..';
import "./MvPointsCalculatorModal.sass"
type Tip = {
    label: string;
    desc: string;
};

type Options = {
    min: number;
    max: number;
}
export interface Props {
    show: boolean;
    toggle: () => void;
    localize: any;
    criteria: SearchCriteria;
    form: CalculatorFormData;
    onChange: (name: string, value: any) => void;
    removeResorts: (removeResorts: string[]) => void;
    onClearSearch: () => void;
    onSubmit: (payload: CalculatorFormData) => void;
    searchResult: ResortPointInterface[];
    searchRequestStatus: RequestStatus;
    selectedCalculatorFilter: CalculatorFormData;
    apiTimeout: string;
};

/**
 * TODO: Describe this component!
 */
const MvPointsCalculatorModal = ({
    show,
    toggle,
    localize,
    criteria,
    form,
    onChange,
    removeResorts,
    onClearSearch,
    onSubmit,
    searchResult,
    searchRequestStatus,
    selectedCalculatorFilter,
    apiTimeout
}: Props) => {
    const defaultTableSortStates = {
        [SortType.DESTINATION]: SortDirection.ASC,
        [SortType.VIEWTYPE]: SortDirection.ASC,
        [SortType.ROOMTYPE]: SortDirection.ASC
    };
    const [tableSortStates, setTableSortStates] = useState<{
        [SortType.DESTINATION]: SortDirection,
        [SortType.VIEWTYPE]: SortDirection,
        [SortType.ROOMTYPE]: SortDirection
    }>(defaultTableSortStates);
    const [tableData, setTableData] = useState<ResortPointInterface[]>(searchResult);
    const isMobile = navigator.userAgent.indexOf("Mobi") > -1;
    const resortData: any[] = criteria["resorts"];
    const numberOfResortsSelected = Object.keys(form["resorts"]).length;
    const nightsDisabled = !form["startDate"];
    const formRequirements = {
        startDate: ["required"],
        numNights: ["required", "max", "min", { max: 21, min: 1 }],
        resorts: ["required"],
        numGuests: ["required", "max", "min", { max: 21, min: 1 }],
    };

    useEffect(() => {
        setTableData(searchResult);
    }, [searchResult])

    const required = (value: any) => {
        if (value.constructor === Object) {
            return Object.entries(value).length !== 0;
        } else if (value.constructor === Array) {
            return value[0] ? true : false;
        }
        return value ? true : false;
    }

    const min = (value: Object, options: any): boolean => {
        return value >= options["min"];
    }

    const max = (value: Object, options: Options): boolean => {
        return value <= options["max"];
    }
    const validators = {
        required: required,
        max: max,
        min: min
    };

    const validate = (formValidation: any, currentForm: any) => {
        return Object.keys(formValidation).filter(field => {
            let currentVal = currentForm[field];
            let validators = formValidation[field];
            let options =
                typeof validators[validators.length - 1] === "object"
                    ? validators[validators.length - 1]
                    : {};
            return !validators
                .map((val: Validators) => {
                    if (typeof val === "string") {
                        let func: Validators["func"] = validators[val];
                        if (typeof func === "function")
                            return func(currentVal, options);
                    }
                    return true;
                })
                .every((val: boolean) => {
                    return true === val;
                });
        });
    }

    const validForm = !(
        validate(formRequirements, form).length === 0
    );

    const resortDisplay = (value: number, max: string, of: string, msg: string, add: string): string => {
        if (value === 0) {
            return "";
        } if (value >= 1 && value <= 14) {
            return `${value} ${msg}, ${add}`;
        } else {
            return `${max} ${of} ${value} ${msg}`;
        }
    }

    const handleInput = (e: any): void => {
        const target = e.target;
        let value;
        let name = target.name;

        // html checkbox
        if (target.type === "checkbox") {
            value = target.checked;
        }
        // checkbox wrapper
        else if ((target.attributes && target.attributes.role && target.attributes.role.value === "checkbox")) {
            name = target.dataset.name;
            value = !form[name];
        } else {
            value = target.value;
        }
        onChange(name, value);

        if (target.name === 'flexibleSearch' && target.type === "checkbox") {
            onChange("startDate", "");
        }
    }

    const onSortClicked = (sortField: SortType) => {
        const resorts = [...tableData];
        const sortDirection = tableSortStates[sortField] === SortDirection.ASC ?
            SortDirection.DESC : SortDirection.ASC;

        const sorteResorts = sortResortPointCalculator(
            resorts,
            sortField,
            sortDirection
        );

        const updatedTableSortStates = { ...tableSortStates };

        // update the sort direction of the sort field that was clicked
        updatedTableSortStates[sortField] = sortDirection;

        // reset other sort directions to ASC (all sort icons will return to ASC)
        if (sortField !== SortType.DESTINATION) {
            updatedTableSortStates[SortType.DESTINATION] = SortDirection.ASC;
        }
        if (sortField !== SortType.VIEWTYPE) {
            updatedTableSortStates[SortType.VIEWTYPE] = SortDirection.ASC;
        }
        if (sortField !== SortType.ROOMTYPE) {
            updatedTableSortStates[SortType.ROOMTYPE] = SortDirection.ASC;
        }
        setTableData(sorteResorts);
        setTableSortStates(updatedTableSortStates);
    }

    const performActionOnSpace = (event: React.KeyboardEvent<HTMLElement>, action: any) => {
        if (event.keyCode == 32) {
            event.preventDefault()
            action()
        }
    }

    const handleSubmit = (): void => {
        if (onSubmit) {
            onSubmit(form);
        }
    }
    const getHeaders = (): any[] => {
        let headers = [
            {
                text: (
                    <span className="destination-header">
                        {localize("Destination")}
                    </span>
                ),
                sortable: true,
                curSortDirection: tableSortStates[SortType.DESTINATION],
                onSort: () => {
                  onSortClicked(SortType.DESTINATION);
                }
            }
        ]
        if (!isMobile) {
            headers.push({
                text: (
                    <span className="room-type-header">
                        {localize("static.content.room-type")}
                    </span>
                ),
                sortable: true,
                curSortDirection: tableSortStates[SortType.ROOMTYPE],
                onSort: () => {
                  onSortClicked(SortType.ROOMTYPE);
                }
            });
            headers.push({
                text: (
                    <span className="view-type-header">
                        {localize("View Type")}
                    </span>
                ),
                sortable: true,
                curSortDirection: tableSortStates[SortType.VIEWTYPE],
                onSort: () => {
                  onSortClicked(SortType.VIEWTYPE);
                }
            });
        }
        return headers;
    };

    const getDestination = (item: ResortPointInterface) => {
        return <div>
            <p className='resort-name'>{item.name}</p>
            <p className='resort-location'>{item.resortLocation}</p>
            {!isMobile && <p className='activity-icons'>{item.activities.map((activity) => {
                let url = activity.iconURL;
                return (
                    <div key={url} className="icon">
                        <img className="activity-icon" src={url} alt="" />
                        <p className="activity-icon-text">{activity.name}</p>
                    </div>
                );
            })}</p>}
            {isMobile && <div className='resort-and-view-type'>
                <div><b>Room Type:</b> <u>{item.room}</u></div>
                <div><b>View Type:</b> <span>{item.viewDescription}</span></div>
            </div>}
        </div>
    }

    const getRows = (): TableRow[] => {
        return tableData.map((item, i) => {
            const rowCells = {
                destination: getDestination(item)
            }
            if (!isMobile) {
                (rowCells as any)['roomType'] = <p className='room-type'>{item.room}</p>;
                (rowCells as any)['viewType'] = <p className='view-type'>{item.viewDescription}</p>;
            }
            return {
                rowKey: `points-calculator-${i}`,
                rowCells,
                rowExpanded: (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px'
                    }}>
                        <PointsCalendar
                            startDate={selectedCalculatorFilter.startDate}
                            data={item.daily}
                            activeDate={null}
                        />
                        <div className='pointsCalendarInformation'>
                            <div className='calendarSelectLabel'>Select Check-In date on calender</div>
                            <div className='calendarNights'><div className='calendarNightsLabel'><b>Total for:</b> {selectedCalculatorFilter.numNights} nights</div> <div className='calendarNightsTextBox'><MvTextOrEdit
                                id={`total-points-for-nights-${i}`}
                                name={`total-points-for-nights-${i}`}
                                onChange={(val: string) => console.log(val)}
                                label=''
                                isEditing={true}
                                onBlur={(e) => { }}
                                value={''}
                                placeholder={''}
                                hideLabel={true}
                                hasError={false}
                                errorMessage={''}
                                isRequired={false}
                                inputComponent='text'
                            /></div></div>
                            <div className='calendarAvailablityButton'><button className="form-action-button mv-button primary" type="button"
                                onClick={() => { }}>{localize("static.content.check-availability")}</button></div>
                        </div>
                    </div>
                )
            };
        });
    }

    const tableHeaders = getHeaders();
    const tableRows = getRows();

    return (
        <div className="mv-points-calculator-modal">
            <MvModal openModal={show} toggleModal={toggle}>
                <div className="mv-points-calculator-modal-container">
                    <div className="mv-calculator-head">
                        <div className="mv-calculator-modal-header">{localize('Marriott Vacation Club Points Calculator')}</div>
                        <p className="mv-calculator-modal-sub-header">Before you start your booking process, use the Points Calculator to see how many points are needed to plan your vacation. The points calculator does not check availability.</p>
                        <p className="mv-calculator-modal-sub-header"><i>Field marked with an asterisk (*) are required</i></p>
                    </div>
                    <div className="mv-calculator-body">
                        <div className="row mv-calculator-row1">
                            <div className="resorts-dropdown-calc column">
                                <ResortDropdown
                                    name="resorts"
                                    limit={15}
                                    activityFilter={""}
                                    selected={form["resorts"]}
                                    value={resortDisplay(
                                        numberOfResortsSelected,
                                        localize('static.content.max'),
                                        localize('static.content.of'),
                                        localize('static.content.num-resorts-selected'),
                                        localize('static.content.click-to-add')
                                    )
                                    }
                                    onRemove={removeResorts}
                                    required={true}
                                    onChange={handleInput}
                                    resortData={resortData}
                                    tabIndex={1}
                                />
                            </div>
                            <div className="search-month-calc column">
                                <MvSelect
                                    name="startDate"
                                    onChange={handleInput}
                                    label={localize('static.content.search-month')}
                                    required={true}
                                    value={form["startDate"]}
                                    items={criteria.searchMonths}
                                    tabIndex={2}
                                />
                            </div>
                        </div>
                        <div className="row mv-calculator-row2">
                            <div className='column nights-and-guest-numbers-calc'>
                                <div className="row">
                                    <div className="nights-number-calc column">
                                        <MvSpinner
                                            min={1}
                                            max={21}
                                            isMobile={isMobile}
                                            disabled={nightsDisabled}
                                            onChange={handleInput}
                                            required={true}
                                            softError={localize('static.content.warning.max-num-of-nights')}
                                            hardError={localize('static.content.warning.max-num-of-nights')}
                                            name="numNights"
                                            value={form["numNights"]}
                                            placeholder={localize('static.content.num-of-nights')}
                                            label={localize('static.content.num-of-nights')}
                                            tabIndex={3}
                                            getText={(num) => {
                                                return `${num} ${num === 1 ? localize('static.content.night') : localize('static.content.nights')}`;
                                            }}
                                        />
                                    </div>
                                    <div className="guest-number-calc column">
                                        <MvSpinner
                                            max={9}
                                            min={1}
                                            onChange={handleInput}
                                            hardError={localize('static.content.warning.max-num-of-guests')}
                                            softError={localize('static.content.warning.max-num-of-guests')}
                                            errorStyle={{ transform: "translateX(-104%)" }}
                                            disabled={false}
                                            name="numGuests"
                                            isMobile={isMobile}
                                            required={true}
                                            value={form["numGuests"]}
                                            placeholder={localize('static.content.num-of-guests')}
                                            tabIndex={4}
                                            label={localize('static.content.num-of-guests')}
                                            getText={(num) => {
                                                return `${num === 9 ? '9+' : num} ${num === 1 ? localize('static.content.guest') : localize('static.content.guests')}`;
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="calculate-button column">
                                <a role="button" className="form-action-link start-over" tabIndex={5} onClick={onClearSearch} onKeyDown={(event) => performActionOnSpace(event, onClearSearch)}>
                                    {localize('static.content.start-over')}
                                </a>
                                <button className={!validForm ? "form-action-button mv-button primary" : "form-action-button mv-button primary disabled"} tabIndex={6} disabled={validForm} type="button" onClick={handleSubmit}>
                                    <span className="search-carat">{localize('Calculate')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    {tableRows.length > 0 && <div className="mv-calculator-result">
                        <div className='row'>
                            <div className='column result-table'>
                                <MvSortTable
                                    rowKey="roomPoolCode"
                                    headers={tableHeaders}
                                    rows={tableRows}
                                    dataStatus={searchRequestStatus}
                                    headerSection={null}
                                    rowsExpandable={true}
                                    minWidth={tableHeaders.length * 230}
                                    apiTimeout = {apiTimeout}
                                />
                            </div>
                        </div>
                    </div>}
                </div>
            </MvModal>
        </div>
    );
};

export {
    MvPointsCalculatorModal,
};
